// Can this browser receive the daily notification, and if not, WHY NOT.
//
// Pure - it is handed a navigator and a window rather than reaching for them -
// so every case below is a test rather than a device somebody has to own. The
// same split `shell/screens.ts` and `day/day-model.ts` keep.
//
// THE IPHONE CASE IS THE WHOLE REASON THIS FILE EXISTS. Safari on iOS supports
// Web Push only when the site has been added to the Home Screen; in an ordinary
// tab `window.PushManager` is simply not there, and nothing anybody writes can
// change that - it is Apple's restriction, not a gap in this client. So a plain
// "your browser does not support notifications" would be a lie of omission on
// the one platform where the person can fix it in fifteen seconds. It says how,
// instead.
//
// A TOGGLE THAT LIES IS WORSE THAN NO TOGGLE, which is the rule the whole
// setting is built on: nothing that can never fire is ever offered as a switch.

/** What a browser can do about notifications, and what to say about it. */
export type PushSupport =
  /** Everything is present. The setting is a real setting. */
  | { kind: 'ready' }
  /**
   * iOS or iPadOS, in a browser tab. Web Push exists there only for a site that
   * has been added to the Home Screen, so this is an instruction rather than a
   * refusal.
   */
  | { kind: 'ios-needs-install' }
  | { kind: 'ios-needs-update' }
  | { kind: 'ios-installed-unsupported' }
  /** No service worker, no PushManager, no Notification. Nothing to offer. */
  | { kind: 'unsupported' };

/** The pieces of the browser this decision reads, so a test can supply them. */
export interface BrowserFacts {
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  userAgent: string;
  /**
   * `navigator.maxTouchPoints`. Needed only for the iPad, which reports itself
   * as a Macintosh: since iPadOS 13 its user agent says `Macintosh; Intel Mac OS
   * X` and the touch count is the one thing that tells it from a desktop Mac.
   * Getting this wrong tells an iPad owner their browser cannot do it, when in
   * fact adding the site to the Home Screen would work.
   */
  maxTouchPoints: number;
  standalone: boolean;
}

/** Read the facts off a real browser. The only impure line in this file. */
export function browserFacts(nav: Navigator, win: Window): BrowserFacts {
  return {
    serviceWorker: 'serviceWorker' in nav,
    pushManager: 'PushManager' in win,
    notification: 'Notification' in win,
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints,
    standalone:
      (nav as Navigator & { standalone?: boolean }).standalone === true ||
      (typeof win.matchMedia === 'function' &&
        win.matchMedia('(display-mode: standalone)').matches),
  };
}

/**
 * Apple hardware running Safari's engine, which on iOS and iPadOS is every
 * browser - Chrome and Firefox there are WebKit in a different coat, and they
 * inherit the same restriction, so this must not be narrowed to Safari's own
 * user agent string.
 */
function isApplePortable(facts: BrowserFacts): boolean {
  if (/iPhone|iPad|iPod/.test(facts.userAgent)) return true;
  // The iPad claiming to be a Macintosh. A real Mac reports 0 touch points.
  return /Macintosh/.test(facts.userAgent) && facts.maxTouchPoints > 1;
}

function appleOsSupportsPush(facts: BrowserFacts): boolean | null {
  if (!/iPhone|iPad|iPod/.test(facts.userAgent)) return null;
  const match = /OS (\d+)[._](\d+)/.exec(facts.userAgent);
  if (match === null) return null;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  return major > 16 || (major === 16 && minor >= 4);
}

export function pushSupport(facts: BrowserFacts): PushSupport {
  const browserReady = facts.serviceWorker && facts.pushManager && facts.notification;
  if (isApplePortable(facts)) {
    const osSupportsPush = appleOsSupportsPush(facts);
    if (!facts.standalone) {
      if (osSupportsPush === false) return { kind: 'ios-needs-update' };
      return { kind: 'ios-needs-install' };
    }
    if (!browserReady || osSupportsPush === false) return { kind: 'ios-installed-unsupported' };
    return { kind: 'ready' };
  }
  return browserReady ? { kind: 'ready' } : { kind: 'unsupported' };
}

/**
 * The minute of the day a time input carries, or null if it does not carry one.
 *
 * `<input type="time">` gives `HH:MM`, always zero-padded and always 24-hour
 * whatever the field DRAWS - a device set to a 12-hour clock shows "8:00 AM" and
 * still reads `08:00`. Empty is a real value: the field can be cleared.
 */
export function minuteOfTime(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** The other direction, for putting a stored setting back into the field. */
export function timeOfMinute(minute: number): string {
  const clamped = Math.min(Math.max(Math.trunc(minute), 0), 1_439);
  const hours = Math.floor(clamped / 60);
  return `${String(hours).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/**
 * When a person who has never chosen is offered one.
 *
 * EIGHT IN THE MORNING, and it is a default rather than a question: the setting
 * is off until somebody turns it on, and asking them to pick a time before they
 * have seen what the thing does is one decision too many on a screen whose whole
 * argument is that it is already set up (AGENTS.md, "General-audience defaults").
 */
export const DEFAULT_SEND_AT_MINUTE = 8 * 60;

/**
 * The granularity the picker offers, in seconds.
 *
 * FIVE MINUTES, keeping a detailed control calm rather than offering 1,440
 * apparent choices. The server accepts any minute - a client does not get to
 * narrow an API - and scans at least twice per minute so every value is reachable.
 */
export const TIME_STEP_SECONDS = 300;

/**
 * The base64url public key a browser is handed, as the bytes `subscribe` wants.
 *
 * `applicationServerKey` takes a `BufferSource` and not a string, and `atob` is
 * the only decoder in a browser - so the two base64url characters that are not
 * base64 have to be swapped back and the padding put on. A wrong key here is not
 * an error: `subscribe` resolves, the browser registers with the push service,
 * and every notification is then refused with a 403 nobody sees.
 */
export function applicationServerKey(publicKey: string): Uint8Array<ArrayBuffer> {
  const padded = publicKey.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  // Written into a plain `ArrayBuffer` rather than through `Uint8Array.from`,
  // whose result is typed over `ArrayBufferLike` - which `BufferSource` will not
  // accept, because a `SharedArrayBuffer` is not something `subscribe()` takes.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** The reverse, for the two keys a browser hands over as raw `ArrayBuffer`s. */
export function toBase64Url(buffer: ArrayBuffer | null): string {
  if (buffer === null) return '';
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
